/**
 * API endpoints for OTA updates
 */

import { IUpdateManifest } from '../models/Update';
import FetchError from './errors';
import { customFetchWithRetryV2 } from './shared';

/**
 * Get update manifest for current platform and app version
 */
export const getUpdateManifest = async (
  platform: string,
  appVersion: string
): Promise<IUpdateManifest | null> => {
  try {
    const response = await customFetchWithRetryV2(
      `/updates/manifest?platform=${platform}&appVersion=${appVersion}`,
      {
        method: 'GET',
      }
    );
    
    if (response.ok) {
      return response.json();
    }

    // Return null if no updates available (404) instead of throwing
    if (response.status === 404) {
      return null;
    }

    throw new FetchError(`Error fetching update manifest. ${await response.text()}`, response.status);
  } catch (e) {
    // Don't throw for network errors, just return null
    console.error('Error fetching update manifest:', e);
    return null;
  }
};

/**
 * Report successful update installation
 */
export const reportUpdateInstalled = async (
  updateType: string,
  version: string
): Promise<void> => {
  try {
    await customFetchWithRetryV2(
      `/updates/installed`,
      {
        method: 'POST',
        body: JSON.stringify({ updateType, version }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (e) {
    // Don't throw, just log
    console.error('Error reporting update installation:', e);
  }
};

/**
 * Report update installation failure
 */
export const reportUpdateFailed = async (
  updateType: string,
  version: string,
  error: string
): Promise<void> => {
  try {
    await customFetchWithRetryV2(
      `/updates/failed`,
      {
        method: 'POST',
        body: JSON.stringify({ updateType, version, error }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (e) {
    // Don't throw, just log
    console.error('Error reporting update failure:', e);
  }
};
